/**
 * Frontend wrapper for the `/ocr` Cloud Function.
 *
 * Reads endpoint from `VITE_OCR_ENDPOINT` and sends the user's Google access
 * token (held by `@/lib/auth/session` — the SINGLE source of truth) as
 * `Authorization: Bearer <token>`.
 *
 * Cold-start UX (elapsed-second indicator) is the caller's job;
 * this wrapper just resolves with the parsed results.
 */

import { getToken as getSessionToken } from '@/lib/auth/session';
import { fetchWithAuth } from '@/lib/auth/gis';
import type { MochiaError } from '@/lib/errors';

export interface OcrImage {
  id: string;
  data: string; // base64
  mime: string;
}

/** A category sent to the OCR backend so the model picks a real id.
 * `parent_id` is '' for a main category, else the main category's id. */
export interface OcrCategory {
  id: string;
  name: string;
  parent_id: string;
}

export interface OcrFields {
  date: string;
  /** Time of purchase `HH:MM` (24h), or '' when the receipt had no readable
   * time. The front-end falls back to capture time so the saved transaction
   * always carries a `THH:MM` component for stable sorting. */
  time: string;
  merchant: string;
  amount_minor: number;
  currency: string;
  /** Real main-category id chosen by the model from the taxonomy we sent, or
   * '' when none/illegal. The front-end re-validates before using it. */
  category_id: string;
  /** Real sub-category id (belongs to category_id), or ''. */
  subcategory_id: string;
}

export interface OcrResultOk {
  id: string;
  status: 'ok';
  /** One image can yield multiple receipts (e.g. a stack of receipts in one
   * photo). Always non-empty when status='ok'. Length 1 is the typical case. */
  receipts: OcrFields[];
}

export interface OcrResultFail {
  id: string;
  status: 'failed';
  error: string;
}

export type OcrResult = OcrResultOk | OcrResultFail;

export class MissingTokenError extends Error implements MochiaError {
  readonly hint: string;
  constructor() {
    super(
      'Session expired or sign-in incomplete; please sign in via the Google button. If this keeps happening, check VITE_GOOGLE_CLIENT_ID in .env.local and restart the dev server.',
    );
    this.name = 'MissingTokenError';
    this.hint =
      '請點 Google 登入按鈕重新登入。若反覆出現，檢查 .env.local 的 VITE_GOOGLE_CLIENT_ID 是否設定，並重啟 Vite。';
  }
}

export class MissingEndpointError extends Error implements MochiaError {
  readonly hint: string;
  constructor() {
    super('VITE_OCR_ENDPOINT is not configured.');
    this.name = 'MissingEndpointError';
    this.hint =
      '請在 .env.local 設定 VITE_OCR_ENDPOINT，URL 會在 firebase deploy 完成後出現在 functions 輸出。';
  }
}

/** Reason returned by the `/ocr` Cloud Function in the JSON body. */
export type OcrErrorReason =
  | 'forbidden'
  | 'unauthorized'
  | 'invalid_request'
  | 'rate_limited'
  | 'internal_error'
  | string;

export class OcrRequestError extends Error implements MochiaError {
  readonly status: number;
  /** Parsed `error` field from the response body, when available. */
  readonly reason: OcrErrorReason | null;
  /** Raw response body text (truncated). Useful for diagnostics. */
  readonly bodyText: string | null;
  readonly hint?: string;
  constructor(
    status: number,
    message?: string,
    extras?: { reason?: OcrErrorReason | null; bodyText?: string | null },
  ) {
    super(message ?? `OCR request failed with status ${status}`);
    this.name = 'OcrRequestError';
    this.status = status;
    this.reason = extras?.reason ?? null;
    this.bodyText = extras?.bodyText ?? null;
    if (this.reason === 'forbidden') {
      this.hint = '你的 email 不在 OCR 允許名單，請聯絡管理員加入。';
    } else if (this.reason === 'unauthorized' || status === 401) {
      this.hint = '存取權杖已過期，請重新登入。';
    } else if (status === 429 || this.reason === 'rate_limited') {
      this.hint = '辨識請求過於頻繁，請稍後再試。';
    }
  }
}

function getEndpoint(): string {
  const ep = import.meta.env.VITE_OCR_ENDPOINT;
  if (!ep || typeof ep !== 'string') throw new MissingEndpointError();
  return ep;
}

function getToken(): string {
  const tok = getSessionToken();
  if (!tok) throw new MissingTokenError();
  return tok;
}

function isResult(value: unknown): value is OcrResult {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'string') return false;
  if (v.status === 'ok') {
    return Array.isArray(v.receipts) && v.receipts.length > 0;
  }
  if (v.status === 'failed') return typeof v.error === 'string';
  return false;
}

export async function ocrRecognize(
  images: OcrImage[],
  taxonomy: OcrCategory[] = [],
): Promise<OcrResult[]> {
  const endpoint = getEndpoint();
  // Ensure a token is present before attempting the request.
  // fetchWithAuth handles the 401-retry path internally (via postRefresh).
  getToken(); // throws MissingTokenError if no token in memory

  // fetchWithAuth attaches the bearer token and retries once on 401 using the
  // server-backed refresh endpoint — same pattern as the Sheets client.
  // `categories` lets the backend constrain the model to real category ids so
  // OCR tracks the user's actual (incl. custom) taxonomy.
  const res = await fetchWithAuth(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ images, categories: taxonomy }),
  });

  if (!res.ok) {
    // Read the response body so callers can switch on `error.reason`. The
    // `/ocr` Cloud Function returns `{error: 'forbidden' | 'unauthorized' |
    // ...}` — without exposing this, "your email is off the allowlist" was
    // indistinguishable from "your token is bad".
    const bodyText = await res.text().catch(() => '');
    let reason: OcrErrorReason | null = null;
    let subReason: string | null = null;
    if (bodyText) {
      try {
        const parsed = JSON.parse(bodyText) as { error?: unknown; reason?: unknown };
        if (typeof parsed.error === 'string' && parsed.error.length > 0) {
          reason = parsed.error;
        }
        // The function now also returns a sub-reason field (e.g.
        // tokeninfo_non_2xx, aud_mismatch, not_in_allowlist) so the
        // user-facing toast can be specific instead of "辨識失敗".
        if (typeof parsed.reason === 'string' && parsed.reason.length > 0) {
          subReason = parsed.reason;
        }
      } catch {
        /* not JSON — leave reason null */
      }
    }
    const msgParts = [`OCR request failed (${res.status})`];
    if (reason) msgParts.push(reason);
    if (subReason) msgParts.push(`sub=${subReason}`);
    throw new OcrRequestError(res.status, msgParts.join(': '), {
      reason,
      bodyText: bodyText.length > 1000 ? bodyText.slice(0, 1000) : bodyText,
    });
  }

  const data: unknown = await res.json();
  if (
    !data ||
    typeof data !== 'object' ||
    !Array.isArray((data as { results?: unknown }).results)
  ) {
    throw new OcrRequestError(res.status, 'Malformed OCR response');
  }
  const results = (data as { results: unknown[] }).results;
  for (const r of results) {
    if (!isResult(r)) throw new OcrRequestError(res.status, 'Malformed OCR result entry');
  }
  return results as OcrResult[];
}
